import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  Alert,
  AlertDescription,
} from "@repo/ui";
import { useToast } from "@repo/ui";
import { PasswordConfirmationModal } from "@/components/security/password-confirmation-modal";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { auditInventory } from "@/lib/api/inventory";
import { ApiClientError } from "@/lib/api/client";
import {
  InventorySearchCombobox,
  type InventoryItem as ComboboxInventoryItem,
} from "@/components/forms";
import { AuditCountForm, type AuditFormData } from "./audit-count-form";

interface InventoryItem {
  id: string;
  ndc: string;
  item_name: string;
  stock_calculated: number;
}

interface InventoryAuditModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  inventoryItems: InventoryItem[];
  preselectedItemId?: string | null;
}

export function InventoryAuditModal({
  open,
  onClose,
  onSuccess,
  inventoryItems,
  preselectedItemId,
}: InventoryAuditModalProps) {
  const { toast } = useToast();
  const [selectedItemId, setSelectedItemId] = React.useState<string>("");
  const [selectedItemStock, setSelectedItemStock] = React.useState<number>(0);
  const [selectedItemName, setSelectedItemName] = React.useState<string>("");
  const [displayLabel, setDisplayLabel] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {}
  );
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [pendingFormData, setPendingFormData] =
    React.useState<AuditFormData | null>(null);

  const { requestApproval, isApprovalPending } = useApproval();

  function resetForm() {
    setSelectedItemId("");
    setSelectedItemStock(0);
    setSelectedItemName("");
    setDisplayLabel("");
    setError("");
    setFieldErrors({});
    setShowPasswordModal(false);
    setPendingFormData(null);
  }

  function handleInventorySelect(item: ComboboxInventoryItem | null) {
    if (item) {
      setSelectedItemId(item.id);
      setSelectedItemName(item.itemName);
      setDisplayLabel(item.label);
      const inventoryItem = inventoryItems.find((inv) => inv.id === item.id);
      setSelectedItemStock(inventoryItem?.stock_calculated ?? 0);
      setFieldErrors((prev) => ({ ...prev, item: "" }));
    } else {
      setSelectedItemId("");
      setSelectedItemName("");
      setDisplayLabel("");
      setSelectedItemStock(0);
    }
  }

  function handleAuditFormSubmit(data: AuditFormData) {
    setError("");
    if (!selectedItemId) {
      setFieldErrors({ item: "Please select an item" });
      setError("Please fix the highlighted fields");
      return;
    }
    setPendingFormData(data);
    setShowPasswordModal(true);
  }

  async function submitAudit(password: string, approvalToken?: string) {
    if (!pendingFormData) return;

    setIsLoading(true);

    try {
      const data = await auditInventory({
        inventoryId: selectedItemId,
        auditDate: pendingFormData.auditDate,
        physicalCount: pendingFormData.physicalCount,
        reason: pendingFormData.reason,
        justification: pendingFormData.justification,
        password,
        approvalToken,
      });

      const quantityDifference =
        pendingFormData.physicalCount - selectedItemStock;

      if (data.audit.hasDiscrepancy) {
        toast({
          title: "Audit Complete \u2014 Adjustment Recorded",
          description: `${selectedItemName}: physical ${pendingFormData.physicalCount} (${quantityDifference > 0 ? "+" : ""}${quantityDifference}). Adjustment saved.`,
          variant: "success",
        });
      } else {
        toast({
          title: "Audit Complete \u2014 Counts Match",
          description: `${selectedItemName}: physical count of ${pendingFormData.physicalCount} confirmed.`,
          variant: "success",
        });
      }

      setIsLoading(false);
      resetForm();
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.statusCode === 403) {
        setShowPasswordModal(false);
        setIsLoading(false);
        handlePermissionError(err, {
          requestApproval,
          toast,
          retryFn: (token) => submitAudit(password, token),
          actionDescription: `Inventory audit for ${selectedItemName}`,
        });
        return;
      }

      setIsLoading(false);
      console.error("Inventory audit error:", err);
      toast({
        title: "Audit Failed",
        description:
          err instanceof Error ? err.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  }

  async function handlePasswordConfirmed(password: string) {
    await submitAudit(password);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  // Auto-select item when preselectedItemId is provided
  React.useEffect(() => {
    if (open && preselectedItemId) {
      const item = inventoryItems.find((inv) => inv.id === preselectedItemId);
      if (item) {
        setSelectedItemId(item.id);
        setSelectedItemName(item.item_name);
        setSelectedItemStock(item.stock_calculated);
        setDisplayLabel(`${item.ndc} - ${item.item_name}`);
        setFieldErrors((prev) => ({ ...prev, item: "" }));
      }
    }
  }, [open, preselectedItemId, inventoryItems]);

  return (
    <Dialog open={open && !isApprovalPending} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Inventory Audit</DialogTitle>
          <DialogDescription>
            Verify that the physical count is accurate
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-5">
          {/* Item Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select Item</Label>
            <InventorySearchCombobox
              onSelect={handleInventorySelect}
              placeholder="Search by NDC or item name..."
              searchPlaceholder="Type to search inventory..."
              className={fieldErrors.item ? "border-destructive" : ""}
              displayLabel={displayLabel}
            />
            {fieldErrors.item && (
              <p className="text-sm text-destructive">{fieldErrors.item}</p>
            )}
          </div>

          {/* Audit Form */}
          {selectedItemId && (
            <AuditCountForm
              systemCount={selectedItemStock}
              onSubmit={handleAuditFormSubmit}
              isLoading={isLoading}
              submitLabel="Review & Confirm Audit"
              onCancel={handleClose}
            />
          )}
        </div>

        <PasswordConfirmationModal
          open={showPasswordModal && !isApprovalPending}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={handlePasswordConfirmed}
          title="Confirm Inventory Audit"
          description=""
          actionLabel="Submit Audit"
        />
      </DialogContent>
    </Dialog>
  );
}
